// client/src/components/billingExecution/BillingExecution.js
// Update to add bylaw selection and fix transaction display

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Divider,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import billingTransactionService from '../../services/billingTransactionService';
import billingReceiptService from '../../services/billingReceiptService';
import paymentBylawService from '../../services/paymentBylawService'; // Add this import
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';

const BillingExecution = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    t('selectBylawAndTransaction'),
    t('uploadExcel'),
    t('previewData'),
    t('confirmExecution')
  ];
  
  // Add state for bylaws
  const [bylaws, setBylaws] = useState([]);
  const [selectedBylaw, setSelectedBylaw] = useState('');
  
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [excelData, setExcelData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [billingDueDate, setBillingDueDate] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [noBylawsAvailable, setNoBylawsAvailable] = useState(false);
  
  // Current language
  const lang = i18n.language;
  
  // Load active bylaws on component mount
  useEffect(() => {
    const loadBylaws = async () => {
      try {
        setLoading(true);
        
        // Get current date for expiration check
        const today = new Date();
        
        // Get active bylaws
        const response = await paymentBylawService.getActiveBylaws();
        

        console.log("  getActiveBylaws()  " ,response.data)

        console.log("  user   ", user )
        //console.log("  user.faculty_id   ", user.faculty.id )
        // Filter bylaws:
        // 1. Must be approved
        // 2. Must be current version (is_last = 1)
        // 3. Must not be expired
        // 4. Must be university OR match user's faculty
        const filteredBylaws = response.data.filter(bylaw => 
          bylaw.is_approved && 
          bylaw.is_last && 
          new Date(bylaw.expired_at) > today 
          && (bylaw.bylaw_type === 'university' || user.role === "admin" || bylaw.faculty_id === user.faculty.id)
        );
        
        console.log("  filteredBylaws  " ,filteredBylaws)

        setBylaws(filteredBylaws);
        
        /*
        // Set the no bylaws available flag
        if (filteredBylaws.length === 0) {
          setNoBylawsAvailable(true);
        }
        */

      // Automatically select the first available bylaw
      if (filteredBylaws.length > 0) {
        setSelectedBylaw(filteredBylaws[0].id);
      } else {
        setNoBylawsAvailable(true);
      }

        setLoading(false);
      } catch (err) {
        console.log( 'BillingExecution useEffect --> ',err)
        setError(t('errorLoadingBylaws'));
        setLoading(false);
      }
    };
    
    loadBylaws();
  }, [t, user.faculty_id]);
  
  // Load transactions when bylaw is selected
  useEffect(() => {
    const loadTransactions = async () => {
      if (!selectedBylaw) {
        setTransactions([]);
        setSelectedTransaction('');
        return;
      }
      
      try {
        setLoading(true);
        
        // Get transactions for the selected bylaw
        const response = await billingTransactionService.getBylawTransactions(selectedBylaw);
        setTransactions(response.data);
        
        // Reset selected transaction
        setSelectedTransaction('');
        setTransactionDetails(null);
        
        setLoading(false);
      } catch (err) {
        setError(t('errorLoadingTransactions'));
        setLoading(false);
      }
    };
    
    loadTransactions();
  }, [selectedBylaw, t]);
  
  // Load transaction details when transaction is selected
  useEffect(() => {
    const loadTransactionDetails = async () => {
      if (!selectedTransaction) {
        setTransactionDetails(null);
        return;
      }
      
      try {
        setLoading(true);
        const response = await billingTransactionService.getBillingTransaction(selectedTransaction);
        setTransactionDetails(response.data);
        setLoading(false);
      } catch (err) {
        setError(t('errorLoadingTransactionDetails'));
        setLoading(false);
      }
    };
    
    loadTransactionDetails();
  }, [selectedTransaction, t]);
  
  // Handle bylaw selection
  const handleBylawChange = (e) => {
    setSelectedBylaw(e.target.value);
    setExcelData([]);
    setFileName('');
    setUploadError('');
    setError('');
  };
  
  // Handle transaction selection
  const handleTransactionChange = (e) => {
    setSelectedTransaction(e.target.value);
    setExcelData([]);
    setFileName('');
    setUploadError('');
    setError('');
  };
  
  // Handle date change
  const handleDateChange = (e) => {
    setBillingDueDate(e.target.value);
  };
  
  // Handle file upload with react-dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setFileName(file.name);
    setExcelFile(file);
    setUploadError('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(worksheet);
        
        // Validate required columns
        const requiredColumns = ['nationalID', 'nameEN', 'nameAR', 'additionalValue'];
        const headers = Object.keys(parsedData[0] || {});
        
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        if (missingColumns.length > 0) {
          setUploadError(t('missingRequiredColumns', { columns: missingColumns.join(', ') }));
          return;
        }
        
        // Map data to expected format
        const mappedData = parsedData.map((row, index) => ({
          id: index + 1,
          national_id: row.nationalID,
          name_en: row.nameEN,
          name_ar: row.nameAR,
          additional_value: parseFloat(row.additionalValue) || 0
        }));
        
        setExcelData(mappedData);
      } catch (err) {
        setUploadError(t('errorParsingExcel'));
      }
    };
    
    reader.onerror = () => {
      setUploadError(t('errorReadingFile'));
    };
    
    reader.readAsArrayBuffer(file);
  }, [t]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  };
  
  // Handle next step
  const handleNext = () => {
    // Validation for each step
    if (activeStep === 0) {
      if (!selectedBylaw) {
        setError(t('selectBylawRequired'));
        return;
      }
      
      if (!selectedTransaction) {
        setError(t('selectTransactionRequired'));
        return;
      }
    }
    
    if (activeStep === 1 && excelData.length === 0) {
      setError(t('excelFileRequired'));
      return;
    }
    
    setActiveStep((prevStep) => prevStep + 1);
    setError('');
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };
  


// Update handleSubmit method
const handleSubmit = async () => {
  try {
    setLoading(true);
    
    // Get the selected bylaw
    const bylaw = bylaws.find(b => b.id === parseInt(selectedBylaw));
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('billing_transaction_id', selectedTransaction);
    formData.append('payment_bylaw_id', selectedBylaw);
    
    // Determine the user faculty ID
    // For faculty users - use their faculty ID
    // For admin users - use faculty ID from the bylaw if it's a faculty bylaw
    if (user.role === 'faculty') {
      formData.append('user_faculty_id', user.faculty_id);
    } else if (bylaw && bylaw.bylaw_type === 'faculty' && bylaw.faculty_id) {
      formData.append('user_faculty_id', bylaw.faculty_id);
    }
    
    if (billingDueDate) {
      formData.append('billing_due_date', billingDueDate);
    }
    
    if (excelFile) {
      formData.append('file', excelFile);
    }
    
    const response = await billingReceiptService.createBillingReceipt(formData);
    
    setSuccess(t('billingReceiptCreatedSuccess'));
    setLoading(false);
    
    // Navigate to receipt preview after success
    navigate(`/billing-receipts/preview/${response.data.id}`);
  } catch (err) {
    setError(t('errorCreatingReceipt'));
    setLoading(false);
  }
};

  
  // Calculate totals
  const calculateTotal = () => {
    if (!excelData.length || !transactionDetails) return 0;
    
    const baseTotal = parseFloat(transactionDetails.total_value) * excelData.length;
    const additionalTotal = excelData.reduce((sum, item) => sum + (parseFloat(item.additional_value) || 0), 0);
    
    return baseTotal + additionalTotal;
  };
  
  // Get bylaw name by ID
  const getBylawName = (id) => {
    const bylaw = bylaws.find(b => b.id === parseInt(id));
    return bylaw ? bylaw.name : '';
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('billingExecution')}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {noBylawsAvailable && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('noActiveBylawsAvailable')}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Step 1: Select Bylaw and Transaction */}
            {activeStep === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('selectBylawAndTransaction')}
                </Typography>
                
                {/* Bylaw Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="bylaw-label">{t('paymentBylaw')}</InputLabel>
                  <Select
                    labelId="bylaw-label"
                    value={selectedBylaw}
                    onChange={handleBylawChange}
                    label={t('paymentBylaw')}
                    disabled={noBylawsAvailable}
                  >
                   {/* <MenuItem value="">{t('selectBylaw')}</MenuItem> */}
                    {bylaws.map((bylaw) => (
                      <MenuItem key={bylaw.id} value={bylaw.id}>
                        {bylaw.name} - {t(bylaw.bylaw_type)}
                        {bylaw.faculty && ` - ${bylaw.faculty[`name_${lang}`]}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Transaction Selection */}
                <FormControl fullWidth sx={{ mb: 3 }} disabled={!selectedBylaw || transactions.length === 0}>
                  <InputLabel id="transaction-label">{t('transaction')}</InputLabel>
                  <Select
                    labelId="transaction-label"
                    value={selectedTransaction}
                    onChange={handleTransactionChange}
                    label={t('transaction')}
                  >
                    <MenuItem value="">{t('selectTransaction')}</MenuItem>
                    {transactions.map((transaction) => (
                      <MenuItem key={transaction.id} value={transaction.id}>
                        {transaction.paymentItem?.name} - {formatCurrency(transaction.total_value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label={t('billingDueDate')}
                  type="date"
                  value={billingDueDate}
                  onChange={handleDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ mb: 3 }}
                />
                
                {/* Display details for selected transaction */}
                {transactionDetails && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle1">
                      {t('selectedTransaction')}: {transactionDetails.paymentItem?.name}
                    </Typography>
                    <Typography variant="subtitle2">
                      {t('paymentBylaw')}: {getBylawName(selectedBylaw)}
                    </Typography>
                    <Typography variant="body2">
                      {t('totalValue')}: {formatCurrency(transactionDetails.total_value)}
                    </Typography>
                    <Typography variant="body2">
                      {t('distributionCount')}: {transactionDetails.distributions?.length || 0}
                    </Typography>
                  </Alert>
                )}
                
                {/* Warning if no transactions are available */}
                {selectedBylaw && transactions.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('noTransactionsForBylaw')}
                  </Alert>
                )}
              </Box>
            )}
            
            {/* Rest of the steps remain the same */}
            {/* Step 2: Upload Excel File */}
            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('uploadStudentDataExcel')}
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t('excelUploadInstructions')}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('requiredColumns')}: nationalID, nameEN, nameAR, additionalValue
                  </Typography>
                </Alert>
                
                <Box 
                  {...getRootProps()} 
                  sx={{ 
                    border: '2px dashed #ccc',
                    borderRadius: '4px',
                    p: 3,
                    textAlign: 'center',
                    mb: 3,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <Typography>{t('dropExcelHere')}</Typography>
                  ) : (
                    <Box>
                      <UploadIcon fontSize="large" color="primary" sx={{ mb: 1 }} />
                      <Typography>{t('dragDropExcelOrClick')}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('acceptedFormats')}: .xlsx, .xls
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {uploadError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {uploadError}
                  </Alert>
                )}
                
                {fileName && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography>{t('fileUploaded')}: {fileName}</Typography>
                    <Typography>{t('recordsFound')}: {excelData.length}</Typography>
                  </Alert>
                )}
              </Box>
            )}
            
            {/* Step 3: Preview Data */}
            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('previewBillingData')}
                </Typography>
                
                {excelData.length > 0 ? (
                  <>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography>{t('recordsFound')}: {excelData.length}</Typography>
                      <Typography>{t('baseValuePerStudent')}: {formatCurrency(transactionDetails?.total_value)}</Typography>
                      <Typography>
                        {t('totalAdditionalValue')}: {formatCurrency(excelData.reduce((sum, item) => sum + (parseFloat(item.additional_value) || 0), 0))}
                      </Typography>
                      <Typography fontWeight="bold">
                        {t('grandTotal')}: {formatCurrency(calculateTotal())}
                      </Typography>
                    </Alert>
                    
                    <Typography variant="subtitle1" gutterBottom>
                      {t('sampleDataPreview')} ({Math.min(5, excelData.length)} {t('of')} {excelData.length})
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={2} sx={{ fontWeight: 'bold', mb: 1 }}>
                          <Grid item xs={3}>{t('nationalID')}</Grid>
                          <Grid item xs={3}>{t('nameEN')}</Grid>
                          <Grid item xs={3}>{t('nameAR')}</Grid>
                          <Grid item xs={3}>{t('additionalValue')}</Grid>
                        </Grid>
                        <Divider />
                        
                        {excelData.slice(0, 5).map((row, index) => (
                          <React.Fragment key={index}>
                            <Grid container spacing={2} sx={{ py: 1 }}>
                              <Grid item xs={3}>{row.national_id}</Grid>
                              <Grid item xs={3}>{row.name_en}</Grid>
                              <Grid item xs={3}>{row.name_ar}</Grid>
                              <Grid item xs={3}>{formatCurrency(row.additional_value)}</Grid>
                            </Grid>
                            {index < Math.min(4, excelData.length - 1) && <Divider />}
                          </React.Fragment>
                        ))}
                      </Box>
                    </Paper>
                  </>
                ) : (
                  <Alert severity="warning">
                    {t('noDataToPreview')}
                  </Alert>
                )}
              </Box>
            )}
            
            {/* Step 4: Confirm Execution */}
            {activeStep === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('confirmBillingExecution')}
                </Typography>
                
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography fontWeight="bold">
                    {t('executionWarning')}
                  </Typography>
                  <Typography variant="body2">
                    {t('executionWarningDetails')}
                  </Typography>
                </Alert>
                
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">{t('transaction')}</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {transactionDetails?.paymentItem?.name}
                      </Typography>
                      
                      <Typography variant="subtitle2">{t('paymentBylaw')}</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {getBylawName(selectedBylaw)}
                      </Typography>
                      
                      <Typography variant="subtitle2">{t('baseValuePerStudent')}</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{formatCurrency(transactionDetails?.total_value)}</Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">{t('billingDueDate')}</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {billingDueDate || t('notSpecified')}
                      </Typography>
                      
                      <Typography variant="subtitle2">{t('totalStudents')}</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{excelData.length}</Typography>
                      
                      <Typography variant="subtitle2">{t('totalAdditionalValue')}</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {formatCurrency(excelData.reduce((sum, item) => sum + (parseFloat(item.additional_value) || 0), 0))}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" align="right">
                    {t('grandTotal')}: {formatCurrency(calculateTotal())}
                  </Typography>
                </Paper>
              </Box>
            )}
            
            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={activeStep === 0 ? () => navigate('/billing-receipts') : handleBack}
                disabled={loading}
              >
                {activeStep === 0 ? t('cancel') : t('back')}
              </Button>
              
              <Button
                variant="contained"
                color={activeStep === 3 ? "success" : "primary"}
                onClick={activeStep === 3 ? handleSubmit : handleNext}
                disabled={loading || noBylawsAvailable || (selectedBylaw && transactions.length === 0)}
                startIcon={activeStep === 3 ? <CheckIcon /> : null}
              >
                {activeStep === 3 ? t('executeNow') : t('next')}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default BillingExecution;